import { useEffect, useRef, useState } from 'react'
import {
  TextFilterModalPropsType,
  FilterOperatorsType,
} from '../../../utils/types/tableFilterTypes'
import useOutsideClick from '../../hooks/useOutsideHook'
import { SvgGrayTick } from '../../../assets/images'
import { noop } from 'lodash'
import { filterOperatorValues } from '../../../utils'
import { ShortcutKeys } from '../../../utils/tab-indexing-contants'

export const FilterOperatorsModal = ({
  toggleFilterPopUp = noop,
  filterOperator,
  setFilterOperator,
  inputRef,
  operatorOptions,
}: TextFilterModalPropsType) => {
  const popupRef = useRef<HTMLDivElement>(null)
  useOutsideClick({
    ref: popupRef,
    handler: toggleFilterPopUp,
  })
  const [selectedOperatorIndex, setSelectedOperatorIndex] = useState<
    number | null
  >(null)

  const [highlightedIndex, setHighlightedIndex] = useState<number>(0)
  const listRef = useRef<HTMLDivElement>(null)
  const optionRefs = useRef<(HTMLDivElement | null)[]>([])
  const handleOperatorClick = (
    operator: FilterOperatorsType,
    index: number
  ) => {
    setSelectedOperatorIndex(index)
    setFilterOperator(operator)
    toggleFilterPopUp()
    if (inputRef) {
      if (
        operator.value === filterOperatorValues.IS_NULL ||
        operator.value === filterOperatorValues.IS_NOT_NULL ||
        operator.value === filterOperatorValues.IS_EMPTY_NULL ||
        operator.value === filterOperatorValues.IS_NOT_EMPTY_NOT_NULL
      ) {
        if (inputRef.current) inputRef.current.value = ''
        return
      }
      if (inputRef.current) inputRef.current.disabled = false
      inputRef.current?.focus()
      if (inputRef.current?.tagName === 'DIV') {
        inputRef.current.querySelector('input')?.focus()
      }
    }
  }
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!operatorOptions?.length) return

    if (e.key === ShortcutKeys.ArrowDown) {
      e.preventDefault()
      setHighlightedIndex((prev) =>
        prev === operatorOptions.length - 1 ? 0 : prev + 1
      )
    } else if (e.key === ShortcutKeys.ArrowUp) {
      e.preventDefault()
      setHighlightedIndex((prev) =>
        prev === 0 ? operatorOptions.length - 1 : prev - 1
      )
    } else if (e.key === ShortcutKeys.Enter || e.key === ShortcutKeys.Space) {
      e.preventDefault()
      const selected = operatorOptions[highlightedIndex]
      if (selected) handleOperatorClick(selected, highlightedIndex)
    }
  }

  useEffect(() => {
    const defaultOperatorIndex = operatorOptions?.findIndex(
      (item) => item.value === filterOperator?.value
    )
    if (defaultOperatorIndex !== -1) {
      setSelectedOperatorIndex(defaultOperatorIndex)
    }
    listRef?.current?.focus()
  }, [])

  useEffect(() => {
    optionRefs.current[highlightedIndex]?.scrollIntoView({
      block: 'nearest',
      behavior: 'smooth',
    })
  }, [highlightedIndex])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === ShortcutKeys.Tab) {
        setTimeout(() => {
          const activeElement = document.activeElement
          if (
            popupRef.current &&
            activeElement &&
            !popupRef.current.contains(activeElement)
          ) {
            toggleFilterPopUp()
          }
        }, 0)
      } else if (e.key === ShortcutKeys.Escape) {
        toggleFilterPopUp()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [toggleFilterPopUp])

  return (
    <div
      ref={popupRef}
      className="bg-white max-w-80 rounded-md overflow-hidden m-[-1.5rem]"
    >
      <div
        className="max-h-56 overflow-auto"
        onKeyDown={handleKeyDown}
        tabIndex={0}
        ref={listRef}
      >
        {operatorOptions?.map(
          (operator: FilterOperatorsType, index: number) => {
            return (
              <div
                key={`${operator.value}-${index}`}
                className={`flex items-center gap-2 hover:bg-gray-100 py-2 pl-5 pr-9 ${
                  highlightedIndex === index ? 'bg-gray-100' : ''
                }`}
                onClick={() => handleOperatorClick(operator, index)}
                ref={(el) => (optionRefs.current[index] = el)}
              >
                <SvgGrayTick
                  className={`${
                    selectedOperatorIndex === index ? 'visible' : 'invisible'
                  }`}
                />
                <div
                  className="text-xs font-light whitespace-nowrap"
                  data-qa-id={`table_filter_modal_${operator.label}`}
                >
                  {operator.label}
                </div>
              </div>
            )
          }
        )}
      </div>
    </div>
  )
}
